import { cloneElement } from 'react';

import { assertUtil } from '@pukka/common/utils';
import { CheckboxItem } from '@radix-ui/react-dropdown-menu';

import { SelectOption } from './SelectOption';
import {
  KeyType,
  OptionConfig,
  SelectValue,
  ValueType,
  Size,
  SelectOptionProps,
  SelectOptionGroupProps,
  SelectOptionValueProps,
} from './types';

interface SelectOptionsProps<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>> {
  readonly size: Size;
  readonly disabled?: boolean;
  readonly values: V[];
  readonly onSelect: (key: V, e: Event) => void;
  readonly options?: (OptionConfig<K, V, T> | null)[];
}

function SelectOptions<K extends KeyType, V extends ValueType, T extends SelectValue<K, V>>({
  size,
  disabled,
  values,
  onSelect,
  options,
}: SelectOptionsProps<K, V, T>) {
  if (!options) {
    return null;
  }

  return (
    <>
      {options.map((config, index) => {
        if (!config) {
          return null;
        }

        const {
          props,
          element,
          reference: { key },
        } = config;

        if ((props as SelectOptionProps<T> | undefined)?.type === 'group') {
          assertUtil.assertIsNotUndefinedNorNull<SelectOptionGroupProps>(props);

          return element ? (
            cloneElement(element, { key: index, ...props, size }, props.children)
          ) : (
            <SelectOption key={index} size={size} {...props} />
          );
        }

        // remove the key to be passed down
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { key: _key, ...rest } = props as Exclude<T, K>;
        const valueProps = rest as SelectOptionValueProps<T> | undefined;
        const _disabled = disabled ?? valueProps?.disabled;
        const selected = values.indexOf(key) > -1;

        return (
          <CheckboxItem
            key={index}
            checked={selected}
            // eslint-disable-next-line react/jsx-no-bind
            onSelect={onSelect.bind({}, key)}
            className="group select-none outline-none"
          >
            {element ? (
              cloneElement(
                element,
                {
                  ...valueProps,
                  disabled: _disabled,
                  selected,
                  size,
                },
                valueProps?.children,
              )
            ) : (
              <SelectOption type="value" disabled={_disabled} selected={selected} size={size} {...valueProps} />
            )}
          </CheckboxItem>
        );
      })}
    </>
  );
}

export { SelectOptions };
